import React from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, ArrowRight, ArrowLeft, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/lib/motion";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { CouponBox } from "@/components/CouponBox";
import { FreeShippingBar } from "@/components/FreeShippingBar";
import { imgSrc } from "@/lib/img";

export default function Cart() {
  const { cart, update, remove } = useCart();
  const settings = useSettings();

  return (
    <div data-testid="cart-page" className="min-h-[70vh]">
      <section className="py-16 sm:py-24">
        <div className="container-nb">
          <SectionHead center={false} eyebrow="Your Basket" title="Thoughtfully chosen. Honestly delivered." className="mb-10" />

          {cart.items.length === 0 ? (
            <Reveal>
              <div data-testid="cart-page-empty" className="text-center py-20 bg-white border border-forest/10 rounded-3xl">
                <p className="font-serif text-3xl text-forest">Your basket is empty.</p>
                <p className="text-sm text-mutedgreen mt-3">Nature's finest nutrition is waiting for you.</p>
                <Link to="/collection" data-testid="cart-page-explore-button" className="group inline-flex items-center gap-2.5 mt-8 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold transition-colors duration-300">
                  Explore All Products <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="grid lg:grid-cols-[1.7fr_1fr] gap-10 items-start">
              <div className="space-y-4">
                <FreeShippingBar />
                {cart.items.map((item) => {
                  const tid = item.variant_id ? `${item.product.id}-${item.variant_id}` : item.product.id;
                  return (
                    <Reveal key={item.key}>
                      <div data-testid={`cart-page-item-${tid}`} className="flex gap-5 bg-white border border-forest/10 rounded-2xl p-5">
                        <Link to={`/product/${item.product.id}`} className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-creamdark shrink-0">
                          <img src={imgSrc(item.product.image)} alt={item.product.name} className="w-full h-full object-cover" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">{item.product.collection}</p>
                              <h3 className="font-serif text-xl text-forest mt-1"><Link to={`/product/${item.product.id}`} className="hover:text-gold transition-colors">{item.product.name}</Link></h3>
                              <p className="text-xs text-mutedgreen mt-0.5">{item.variant ? item.variant.label : item.product.unit} · ₹{item.unit_price} each</p>
                            </div>
                            <button data-testid={`cart-page-item-${tid}-remove`} onClick={() => remove(item.product.id, item.variant_id)} aria-label="Remove item" className="p-2 text-mutedgreen hover:text-red-700 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center border border-forest/15 rounded-full">
                              <button data-testid={`cart-page-item-${tid}-decrease`} onClick={() => update(item.product.id, item.qty - 1, item.variant_id)} aria-label="Decrease" className="p-2 px-3 text-forest hover:text-gold transition-colors"><Minus size={14} /></button>
                              <span data-testid={`cart-page-item-${tid}-qty`} className="text-sm font-semibold w-7 text-center">{item.qty}</span>
                              <button data-testid={`cart-page-item-${tid}-increase`} onClick={() => update(item.product.id, item.qty + 1, item.variant_id)} aria-label="Increase" className="p-2 px-3 text-forest hover:text-gold transition-colors"><Plus size={14} /></button>
                            </div>
                            <p className="font-serif text-xl text-forest font-semibold">₹{item.line_total}</p>
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
                <Link to="/collection" data-testid="cart-continue-shopping-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-forest link-underline">
                  <ArrowLeft size={13} /> Continue Shopping
                </Link>
              </div>

              <Reveal delay={0.1}>
                <div data-testid="cart-summary" className="bg-forestdeep grain relative rounded-3xl p-8 text-cream lg:sticky lg:top-28">
                  <h3 className="font-serif text-2xl">Order Summary</h3>
                  <div className="mt-6"><CouponBox /></div>
                  <div className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between text-cream/70"><span>Subtotal ({cart.count} items)</span><span data-testid="cart-summary-subtotal" className="text-cream">₹{cart.subtotal}</span></div>
                    {cart.discount > 0 && (
                      <div className="flex justify-between text-goldlight"><span>Discount ({cart.coupon?.code})</span><span data-testid="cart-summary-discount">− ₹{cart.discount}</span></div>
                    )}
                    <div className="flex justify-between text-cream/70"><span>Shipping</span><span data-testid="cart-summary-shipping" className={cart.shipping > 0 ? "text-cream" : "text-goldlight"}>{cart.shipping > 0 ? `₹${cart.shipping}` : "Free"}</span></div>
                    <div className="border-t border-cream/15 pt-4 flex justify-between items-baseline">
                      <span className="font-semibold">Total</span>
                      <span data-testid="cart-summary-total" className="font-serif text-3xl">₹{cart.total}</span>
                    </div>
                  </div>
                  {settings.coming_soon && (
                    <p data-testid="cart-coming-soon-notice" className="mt-6 text-[11px] leading-relaxed text-goldlight bg-cream/5 border border-goldlight/30 rounded-xl px-4 py-3">
                      {settings.checkout_message}
                    </p>
                  )}
                  {settings.coming_soon ? (
                    <button data-testid="cart-checkout-button" onClick={() => toast.success(settings.checkout_message)} className="mt-5 w-full flex items-center justify-center gap-2 bg-gold hover:bg-goldlight text-white text-xs font-bold uppercase tracking-[0.18em] py-4 rounded-full transition-colors">
                      Store Opening Soon <ArrowRight size={14} />
                    </button>
                  ) : (
                    <Link to="/checkout" data-testid="cart-checkout-button" className="mt-5 w-full flex items-center justify-center gap-2 bg-gold hover:bg-goldlight text-white text-xs font-bold uppercase tracking-[0.18em] py-4 rounded-full transition-colors">
                      Proceed to Checkout <ArrowRight size={14} />
                    </Link>
                  )}
                  <div className="mt-6 space-y-2.5">
                    {[[ShieldCheck, "100% Secure Payments"], [RotateCcw, "Easy Returns & Refunds"], [Truck, "Fast & Reliable Delivery"]].map(([Icon, t]) => (
                      <p key={t} className="flex items-center gap-2.5 text-[11px] text-cream/60"><Icon size={13} className="text-goldlight" /> {t}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
